import { Flex, Tooltip } from '@gmg/gmg-react-components';
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useFormatter from 'src/customHooks/useFormatter';
import useSort from 'src/customHooks/useSort';
import styled from 'styled-components';
import IconProduct from '../../assets/svg/icon_product_install.svg';
import StatusIndicator, { Status } from '../shared/StatusIndicator';

type LicenseOrOptionStatus = 'expired' | 'willExpireSoon' | 'willExpire';

export type LicenseStatus = LicenseOrOptionStatus | 'unlimited';

export type OptionStatus = LicenseOrOptionStatus | 'notShown';
export interface LicenseOption {
    name: string;
    expirationDate: string | undefined;
    status: OptionStatus;
}
export interface License {
    name: string;
    expirationDate: string;
    status: LicenseStatus;
    licenseOptions: Array<LicenseOption>;
    installedApplicationDetails: {
        version: string | undefined;
        lastSeen: string | undefined;
    };
}
export interface AccordionProps {
    licenseSystemId: string;
    isExpanded: boolean;
    licenses: Array<License>;
};
interface LicenseDisplayProperties {
    indicator: Status;
    label: string;
}

const wasUsedInLast6Months = (lastSeen: string | undefined) => {
    if (!lastSeen) return false;

    const lastSeenDate = new Date(lastSeen);
    if (isNaN(lastSeenDate.getTime())) return false;

    const thresholdDate = new Date();
    thresholdDate.setMonth(thresholdDate.getMonth() - 6);

    return lastSeenDate.getTime() >= thresholdDate.getTime();
};

const LicensesAccordion: FunctionComponent<AccordionProps> = (props) => {
    const { formatDate } = useFormatter();
    const { getSortFn } = useSort();
    const sortByNameFn = getSortFn('name', 'ASC');
    const { t } = useTranslation();
    const [shouldRender, setShouldRender] = useState(false);

    useEffect(() => {
        if (props.isExpanded) {
            setShouldRender(true);
        }
    }, [props.isExpanded]);


    const getLicenseStatus = (licenseExpirationDateString: string, licenseStatus: LicenseStatus) => {
        const displayProps = new Map<LicenseStatus, LicenseDisplayProperties>([
            ['expired', {
                indicator: 'error',
                label: t('LicenseManagementTable.license_validity.expired_desc', {
                    defaultValue: 'Expired {{date}}',
                    date: licenseExpirationDateString,
                }),
            }],
            ['willExpireSoon', {
                indicator: 'warning',
                label: t('LicenseManagementTable.license_validity.will_expire_desc', {
                    defaultValue: 'Expires on {{date}}',
                    date: licenseExpirationDateString,
                }),
            }],
            ['willExpire', {
                indicator: 'success',
                label: t('LicenseManagementTable.license_validity.will_expire_desc', {
                    defaultValue: 'Expires on {{date}}',
                    date: licenseExpirationDateString,
                }),
            }],
            ['unlimited', {
                indicator: 'success',
                label: t('LicenseManagementTable.license_validity.unlimited', 'Umlimited'),
            }],

        ]);

        return displayProps.get(licenseStatus)!;
    };

    const getOptionStatus = (optionExpirationDateString: string, optionsStatus: OptionStatus) => {
        const displayPropsForOptions = new Map<OptionStatus, string>([
            ['notShown', ''],
            ['expired', t('LicenseManagementTable.license_validity.expired_desc', {
                defaultValue: 'Expired {{date}}',
                date: optionExpirationDateString,
            })],
            ['willExpireSoon', t('LicenseManagementTable.license_validity.will_expire_desc', {
                defaultValue: 'Expires on {{date}}',
                date: optionExpirationDateString,
            })],
            ['willExpire', t('LicenseManagementTable.license_validity.will_expire_desc', {
                defaultValue: 'Expires on {{date}}',
                date: optionExpirationDateString,
            })],
        ]);

        return displayPropsForOptions.get(optionsStatus)!;
    };


    const licenses = !shouldRender ? [] : props.licenses?.sort(sortByNameFn).map(license => {
        const formattedLicenseExpirationDate = formatDate(license.expirationDate);
        const licenseStatus = getLicenseStatus(formattedLicenseExpirationDate, license.status);
        const options = license.licenseOptions
            .sort(sortByNameFn)
            .map((option: LicenseOption) => ({
                name: option.name,
                statusLabel: getOptionStatus(formatDate(option.expirationDate), option.status),
            }));

        return ({
            name: license.name,
            statusIndicator: licenseStatus.indicator,
            statusLabel: licenseStatus.label,
            options,
            installedAppVersion: wasUsedInLast6Months(license.installedApplicationDetails.lastSeen)
                ? license.installedApplicationDetails?.version
                : undefined,
        });
    });

    return (
        <AccordionContainer
            className="vertical-scrollbar"
            aria-expanded={props.isExpanded}
            aria-hidden={!props.isExpanded}
            aria-label={`Collapsible options info for license: ${props.licenseSystemId}`}
            isExpanded={props.isExpanded}
            onAnimationEnd={() => {
                if (!props.isExpanded) {
                    setShouldRender(false);
                }
            }}
        >
            {licenses.map((license, licenseIndex) => {
                return (
                    <LicenseContainer aria-label={`license ${license.name}`} key={`${props.licenseSystemId}-${licenseIndex}`}>
                        <LicenseRow>
                            {/* Longest license name: GMG OpenColor Conventional Multicolor (Subscription) */}
                            <Flex gap="8px">
                                <StyledIconProduct />
                                <Tooltip message={license.name}>
                                    <LicenseNameCell className="px12_400_normal">
                                        {license.name}
                                    </LicenseNameCell>
                                </Tooltip>
                            </Flex>
                            <Flex className="px12_400_normal" alignItems='start'>
                                <Flex gap="5px" alignItems="center">
                                    <StatusIndicator statusClass={license.statusIndicator} />
                                    {license.statusLabel}
                                </Flex>
                            </Flex>
                            {/* The empty boxes are required to align the license accordion with the license system row. */}
                            <Flex flexDirection='column' gap='8px'>
                                <div className="px12_400_normal" aria-label={`installed application info for ${license.name}`}>
                                    {license.installedAppVersion
                                        ? t('LicenseAccordion.installed_app_status_title', 'Installed')
                                        : t('LicenseAccordion.unknown_app_status_title', 'Unknown')
                                    }
                                </div>
                                <div className="px10_400_normal" aria-label={`installed application version for ${license.name}`}>
                                    {license.installedAppVersion
                                        ? t('LicenseAccordion.installed_version_title', {
                                            defaultValue: 'Version {{version}}',
                                            version: license.installedAppVersion,
                                        })
                                        : t('LicenseAccordion.no_install_info_title', 'No installation information available')}
                                </div>
                            </Flex>
                            <div></div>
                            <div></div>
                            <div></div>
                            <div></div>
                        </LicenseRow>

                        <Flex flexDirection="column" pl="24px" gap="6px">
                            {license.options.map((option, optionIndex) => {

                                return (
                                    <LicenseOptionRow key={`${licenseIndex}-${optionIndex}`} className="px10_400_normal">
                                        <div>{option.name}</div>
                                        <div >{option.statusLabel}</div>
                                        {/* The empty boxes are required to align the license accordion with the license system row. */}
                                        <div></div>
                                        <div></div>
                                        <div></div>
                                        <div></div>
                                        <div></div>
                                    </LicenseOptionRow>
                                );
                            },
                            )}
                        </Flex>
                    </LicenseContainer>
                );
            })}
        </AccordionContainer >
    );
};

const AccordionContainer = styled.div<{ isExpanded: boolean }>`
    background-color: ${props => props.theme.colors.backgroundPaper};
    opacity: ${props => (props.isExpanded ? 1 : 0)};
    z-index: ${props => (props.isExpanded ? 1 : -10)};
    max-height: ${props => (props.isExpanded ? 1500 : 0)}px;
    transition: max-height 0.3s ease-in-out, opacity 0.3s ease-in-out;
    overflow: hidden;
`;

const LicenseContainer = styled.div`
    display: flex;
    flex-direction: column;
    padding: 16px 0 16px 52px;
    border-bottom-style: solid;
    border-bottom-width: 2px;
    border-bottom-color: ${props => props.theme.colors.greyContrastLow};
`;

const LicenseRow = styled.div`
    display: grid;
    /* First and second column should be aligned with the licenses accordion */
    grid-template-columns: minmax(100px, 3fr) repeat(3, minmax(100px, 2fr)) repeat(1, minmax(50px, 1fr)) repeat(2, minmax(100px, 1fr));
    //grid-template-columns: minmax(200px, 3fr) repeat(3, minmax(200px, 2fr)) repeat(1, minmax(100px, 1fr)) repeat(2, minmax(200px, 1fr));
    column-gap: 20px;
    align-items: stretch;
`;

const LicenseNameCell = styled.div`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const LicenseOptionRow = styled.div`
    display: grid;
    /* First and second column should be aligned with the licenses accordion */
    grid-template-columns: minmax(200px, 4fr) repeat(4, minmax(200px, 2fr)) repeat(2, minmax(100px, 1fr)) minmax(200px, 1fr);;
    column-gap: 20px;
    align-items: stretch;
`;

const StyledIconProduct = styled(IconProduct)`
    color: ${props => props.theme.colors.textWeak};
    flex-shrink: 0;
`;

export default LicensesAccordion;